import { motion } from 'framer-motion';
import { Calendar, Phone, Mail, ArrowRight, CheckCircle, Clock, Users, Target, Zap, Shield } from 'lucide-react';

const InsuranceCTA = () => {
  const benefits = [
    {
      icon: Clock,
      title: "30-Day Risk Assessment",
      description: "Comprehensive evaluation of your current operations and opportunities"
    },
    {
      icon: Target,
      title: "Custom ROI Projections",
      description: "Detailed financial analysis tailored to your specific situation"
    },
    {
      icon: Users,
      title: "Strategic Roadmap",
      description: "Step-by-step transformation plan with clear timelines and milestones"
    },
    {
      icon: Shield,
      title: "Zero Risk Consultation",
      description: "No commitment required - explore possibilities with our experts"
    }
  ];

  const ctaOptions = [
    {
      type: "primary",
      title: "Book Strategic Consultation",
      subtitle: "Schedule a 45-minute session with our insurance experts",
      icon: Calendar,
      features: [
        "Comprehensive needs assessment",
        "Custom ROI analysis and projections",
        "Detailed implementation roadmap",
        "No obligation strategic recommendations"
      ],
      cta: "Schedule Free Consultation",
      popular: true
    },
    {
      type: "secondary", 
      title: "Request Custom Proposal",
      subtitle: "Get a detailed proposal tailored to your requirements",
      icon: Mail,
      features: [
        "Detailed feature breakdown",
        "Custom pricing based on your needs",
        "Implementation timeline",
        "Reference case studies"
      ],
      cta: "Request Proposal",
      popular: false
    },
    {
      type: "tertiary",
      title: "Speak with an Expert",
      subtitle: "Direct line to our insurance transformation specialists",
      icon: Phone,
      features: [
        "Immediate expert consultation",
        "Answer your specific questions",
        "Discuss implementation challenges",
        "Explore partnership opportunities"
      ],
      cta: "Call Now: +971 4 XXX XXXX",
      popular: false
    }
  ];

  const urgencyFactors = [
    "Every day of delay costs an average of AED 50,000 in missed savings",
    "Fraud losses increase by 15% annually without detection systems",
    "Manual processes cost 3x more than automated alternatives",
    "Member satisfaction drops 2% monthly with poor service experiences"
  ];

  const guarantees = [
    { 
      title: "ROI Guarantee", 
      description: "Measurable ROI within 90 days or we'll work for free until you achieve it",
      icon: Target
    },
    { 
      title: "Implementation Promise", 
      description: "Go-live within 30 days or receive a 25% discount on first year",
      icon: Zap
    },
    { 
      title: "Success Commitment", 
      description: "Dedicated support until you achieve your strategic objectives",
      icon: CheckCircle
    }
  ];

  return (
    <section id="cta" className="py-20 bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#03045E] text-white relative overflow-hidden">
      
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#90E0EF]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#CAF0F8]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <Zap className="w-4 h-4 text-[#CAF0F8] mr-2" />
            <span className="text-[#CAF0F8] font-semibold text-sm">Transform Your Insurance Operations Today</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Don't Let Another Day of <span className="text-[#90E0EF]">Losses</span> Pass By
          </h2>
          
          <p className="text-xl text-[#CAF0F8] max-w-4xl mx-auto leading-relaxed mb-8">
            While you're reading this, fraudulent claims and operational inefficiencies are draining your budget. 
            Take action now and start saving within 30 days.
          </p>

          {/* Urgency Stats */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {urgencyFactors.map((factor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
              >
                <p className="text-sm text-[#CAF0F8] leading-tight">{factor}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Main CTA Options */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {ctaOptions.map((option, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative ${
                option.popular 
                  ? 'bg-white text-[#03045E] transform lg:scale-105' 
                  : 'bg-white/10 backdrop-blur-sm border border-white/20'
              } rounded-3xl p-8 hover:shadow-2xl transition-all duration-300`}
            >
              {/* Popular Badge */}
              {option.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#90E0EF] text-[#03045E] px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                  option.popular ? 'bg-[#90E0EF]/20' : 'bg-white/20'
                }`}>
                  <option.icon className={`w-8 h-8 ${
                    option.popular ? 'text-[#0077B6]' : 'text-[#CAF0F8]'
                  }`} />
                </div>
                
                <h3 className={`text-2xl font-bold mb-2 ${
                  option.popular ? 'text-[#03045E]' : 'text-white'
                }`}>
                  {option.title}
                </h3>
                <p className={`text-sm ${
                  option.popular ? 'text-gray-600' : 'text-[#CAF0F8]'
                }`}>
                  {option.subtitle}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {option.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start space-x-3">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      option.popular ? 'text-green-500' : 'text-[#90E0EF]'
                    }`} />
                    <span className={`text-sm ${
                      option.popular ? 'text-gray-700' : 'text-[#CAF0F8]'
                    }`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg ${
                  option.popular
                    ? 'bg-[#03045E] text-white hover:bg-[#03045E]/90'
                    : 'bg-white text-[#03045E] hover:bg-white/90'
                }`}
              >
                {option.cta}
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Guarantees */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Our Ironclad Guarantees
            </h3>
            <p className="text-[#CAF0F8] max-w-2xl mx-auto">
              We're so confident in our platform that we back every partnership with concrete guarantees.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {guarantees.map((guarantee, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="bg-[#90E0EF]/20 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <guarantee.icon className="w-8 h-8 text-[#CAF0F8]" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{guarantee.title}</h4>
                  <p className="text-[#CAF0F8] text-sm leading-relaxed">{guarantee.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
            <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
              What You Get When You Take Action Today
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-white/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <benefit.icon className="w-6 h-6 text-[#90E0EF]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{benefit.title}</h4>
                  <p className="text-[#CAF0F8] text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Final Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Stop Losing Money. Start Saving Today.
            </h3>
            <p className="text-xl text-[#CAF0F8] mb-8 leading-relaxed">
              Every minute you delay is costing your organization money. Join the insurance companies 
              that have already transformed their operations and are saving millions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#03045E] px-10 py-5 rounded-xl font-bold text-xl hover:bg-white/90 transition-all duration-300 shadow-2xl inline-flex items-center justify-center space-x-3"
              >
                <Calendar className="w-6 h-6" />
                <span>Book Your Free Strategic Session Now</span>
                <ArrowRight className="w-6 h-6" />
              </motion.button>
            </div>

            <div className="flex items-center justify-center space-x-6 text-[#CAF0F8] text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4" />
                <span>No Risk</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4" />
                <span>No Obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4" />
                <span>Immediate Value</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsuranceCTA;
